import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  ACCOUNT_INIT_COMPLETE,
  ACCOUNT_ACCESS_SHOW_LOGIN,
  ACCOUNT_ACCESS_SHOW_EMAIL_JOIN,
  ACCOUNT_ACCESS_SHOW_REGISTER,
  ACCOUNT_ACCESS_SHOW_FORGOT_PASSWORD,
  SET_RECENTLY_USED_AIRPORT_CODE,
  SAVE_CUSTOMER_SIGNATURE,
  DELETE_CUSTOMER_SIGNATURE,
  SYNC_USER_DETAILS,
  GSI_SCRIPT_LOADED,
  SET_CUSTOMER_VALUE,
  CLEAR_AUTH_ERROR,
  REMOVE_USER_NOTIFICATION,
  VIEW_USER_NOTIFICATIONS,
  TOGGLE_SHOP_AS_MEMBER,
  SUBSCRIPTION_USER_NOTIFICATIONS_UPDATE,
  RESET_CUSTOMER_TRAVELLER_SUBMISSION,
  ADD_USER_CART_ITEMS,
  ADD_USER_CART_CONNECTED_ROOMS,
  CLEAR_PURCHASED_USER_CART_ITEMS,
  FETCH_RECENT_SEARCHES,
  SET_RECENT_SEARCHES,
  SYNC_USER_CART,
  SAVE_USER_CART_ITEMS_FOR_LATER,
  SAVE_ALL_USER_CART_ITEMS_FOR_LATER,
  UNDO_USER_CART_ITEMS_DELETE,
  RESET_USER_CART,
  REMOVE_USER_CART_CONNECTED_ROOM,
  UPDATE_USER_CART_ITEM,
  DELETE_USER_CART_ITEMS,
  HARD_DELETE_USER_CART_ITEMS,
} from 'actions/actionConstants'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  FETCH_PAYMENT_CARDS,
  FETCH_STRIPE_PAYMENT_CARDS,
  FETCH_AVAILABLE_CREDIT,
  FETCH_AVAILABLE_CREDIT_COLLECTION,
  USER_LOGIN,
  USER_LOGOUT,
  USER_REGISTRATION,
  UPDATE_USER_DETAILS,
  UPDATE_ACCOUNT_PARTNERSHIP,
  USER_FORGOT_PASSWORD,
  LINK_PARTNERSHIP_ACCOUNT,
  CHECK_LEGACY_ORDERS_FOR_CURRENT_USER,
  CHECK_USER_EXISTS,
  USER_RESET_PASSWORD,
  FETCH_CUSTOMER_DETAILS,
  DELETE_CUSTOMER_DETAILS,
  UPDATE_CUSTOMER_DETAILS,
  SEND_VERIFICATION_CODE,
  CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW,
  FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION,
  FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD,
  UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION,
  UPDATE_ADD_PHONE_PROMPTED,
  FETCH_USER_NOTIFICATIONS,
  FETCH_CUSTOMER_TRAVELLERS,
  DELETE_CUSTOMER_TRAVELLER,
  SUBMIT_CUSTOMER_TRAVELLER,
  FETCH_USER_CART,
  USER_EMAIL_CONFIRMATION,
  FETCH_USER_NUMBER_OF_REVIEWS,
  UPSERT_REVIEW,
} from 'actions/apiActionConstants'

import { extractTokenFields } from 'lib/jwtToken/extractTokenFields'
import { AnyAction } from 'redux'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import { mapObject, omitKeys } from 'lib/object/objectUtils'
import { NON_DUPLICABLE_ITEM_TYPES } from 'constants/userCart'
import { isAccommodationItem } from 'lib/checkout/checkoutUtils'

export const initialAuthState: App.AuthState = {
  account: {
    addressDetails: {},
    gender: undefined,
    memberId: undefined,
    creditsCollection: { initial: true },
    creditsByCurrency: {},
    balance: 0,
    balanceFetched: false,
    balanceError: false,
    paymentCards: [],
    paymentCardsFetched: false,
    paymentCardsError: {},
    stripePaymentCards: [],
    stripePaymentCardsFetched: false,
    stripePaymentCardsError: {},
    referralProgramEnabled: true,
    isSpoofed: false,
    givenName: '',
    surname: '',
    fullName: '',
    title: undefined,
    email: '',
    dob: '',
    legacyId: undefined,
    legacyPlatform: undefined,
    status: undefined,
    roles: [],
    vendors: [],
    countryCode: undefined,
    postcode: undefined,
    phonePrefix: undefined,
    phone: undefined,
    signupDomain: undefined,
    partnerships: {},
    personContactId: undefined,
    emailVerified: true,
    toggles: {},
    lastLogin: {},
    creditsLedger: [],
    numberOfPurchases: 0,
    customerSupportCode: undefined,
    recentlyUsedAirportCode: undefined,
    subscriberTier: null,
    addPhonePromptedAt: undefined,
    luxPlus: {
      member: {
        /** This is used so spoofed customers can be toggled between buying as a member or as a non member */
        disableMembership: false,
        subscription: {
          item: undefined,
          fetching: false,
          error: undefined,
        },
      },
    },
  },
  fetchingNotifications: false,
  notifications: [],
  notificationsError: undefined,
  users: {},
  accessToken: undefined,
  error: undefined,
  processing: false,
  source: undefined,
  travellerDetailsProcessing: false,
  travellerDetailsFetched: false,
  travellerDetailsError: false,
  accountProcessing: false,
  partnershipProcessing: false,
  partnershipError: undefined,
  ledLegacyOrders: {
    hasAny: false,
    legacyCutoffDate: undefined,
    fetchLegacyOrdersFailed: false,
    hasFetched: false,
  },
  initComplete: false,
  signature: undefined,
  gsiLoaded: false,
  completeAccountBannerDismissed: false,
  domainUserId: undefined,
  recentSearches: [],
  numberOfReviews: undefined,
  travelCompanions: { initial: true, data: EmptyArray },
  customerTravellers: {
    submissionState: {
      submissionProcessing: undefined,
      error: false,
      deletionProcessing: {},
    },
  },
  userCart: {
    items: {},
    itemStates: {},
    connectedRoomOfferIds: [],
    synced: false,
    fetching: false,
  },
}

function authSuccess(state: App.AuthState, action: AnyAction): Partial<App.AuthState> {
  const accessToken = action.data.accessToken ?? state.accessToken
  const { agentId } = extractTokenFields(accessToken)

  const accountData: Partial<App.AuthState> = {
    accessToken,
    account: {
      ...state.account,
      ...action.data.account,
      agentId,
    },
    users: {
      ...state.users,
      // they just logged in...we know the account exists!
      [action.data.account.email]: {
        ...state.users[action.data.account.email],
        exists: true,
      },
    },
    processing: false,
    initComplete: true,
  }

  return ({
    ...accountData,
  })
}

function authClearError(): Partial<App.AuthState> {
  return ({
    error: undefined,
  })
}

function authStart(state: App.AuthState, action: AnyAction): Partial<App.AuthState> {
  return ({
    ...authClearError(),
    processing: true,
    source: action.source,
  })
}

function authProcessing(): Partial<App.AuthState> {
  return ({
    ...authClearError(),
    processing: true,
  })
}

function authError(state: App.AuthState, action: AnyAction): Partial<App.AuthState> {
  return ({
    error: action.error ?? action.data,
    processing: false,
    accountProcessing: false,
  })
}

function userCartBase(state: App.AuthState): App.UserCartState {
  return {
    ...state.userCart,
    synced: false,
  }
}

function filterConnectedRooms(items: App.UserCartState['items'], connectedRoomOfferIds: Array<string>) {
  const itemOfferIds = new Set(Object.values(items).filter(isAccommodationItem).map(item => item.offerId))
  return connectedRoomOfferIds.filter(id => itemOfferIds.has(id))
}

const apiCalls = apiReducerSwitch<App.AuthState>({
  [CHECK_USER_EXISTS]: {
    request: authProcessing,
    success: (state, action) => ({
      processing: false,
      users: {
        ...state.users,
        [action.key]: {
          id: action.key,
          exists: action.data.isExist,
          maskedPhone: action.data.maskedPhone,
          email: action.email,
          phoneNumber: action.phoneNumber,
        },
      },
    }),
    failure: authError,
  },
  [USER_LOGIN]: {
    request: authStart,
    success: authSuccess,
    failure: authError,
  },
  [USER_REGISTRATION]: {
    request: authStart,
    success: authSuccess,
    failure: authError,
  },
  [USER_EMAIL_CONFIRMATION]: {
    request: authProcessing,
    success: () => ({
      processing: false,
    }),
    failure: authError,
  },
  [USER_RESET_PASSWORD]: {
    request: authProcessing,
    success: () => ({
      processing: false,
    }),
    failure: authError,
  },
  [USER_FORGOT_PASSWORD]: {
    request: authProcessing,
    success: () => ({
      processing: false,
    }),
    failure: authError,
  },
  [LINK_PARTNERSHIP_ACCOUNT]: {
    request: authProcessing,
    success: (state, action) => ({
      processing: false,
      account: {
        ...state.account,
        partnerships: {
          ...state.account.partnerships,
          [action.prefix]: action.details,
        },
      },
    }),
    failure: authError,
  },
  [SEND_VERIFICATION_CODE]: {
    request: authProcessing,
    success: () => ({
      processing: false,
    }),
    failure: authError,
  },
  [UPDATE_USER_DETAILS]: {
    request: () => ({
      accountProcessing: true,
    }),
    success: (state, action) => ({
      ...authClearError(),
      processing: false,
      accountProcessing: false,
      account: {
        ...state.account,
        ...action.data,
      },
    }),
    failure: authError,
  },
  [UPDATE_ACCOUNT_PARTNERSHIP]: {
    request: () => ({
      partnershipProcessing: true,
    }),
    success: (state, action) => ({
      partnershipProcessing: false,
      account: {
        ...state.account,
        partnerships: {
          ...state.account.partnerships,
          [action.partnershipPrefix]: {
            ...state.account.partnerships[action.partnershipPrefix as keyof App.PartnershipsMap],
            ...action.data,
          },
        },
      },
    }),
    failure: (state, action) => ({
      partnershipProcessing: false,
      partnershipError: action.error,
    }),
  },
  [FETCH_AVAILABLE_CREDIT]: {
    request: (state, action) => ({
      account: {
        ...state.account,
        creditsByCurrency: {
          ...state.account.creditsByCurrency,
          [action.currencyCode]: {
            balance: 0,
            nextExpiringCredit: undefined,
            loading: true,
            error: null,
          },
        },
      },
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        balance: action.data.balance ?? 0,
        balanceFetched: true,
        balanceError: false,
        creditsLedger: action.data.credits,
        creditsByCurrency: {
          ...state.account.creditsByCurrency,
          [action.currencyCode]: {
            balance: action.data.balance,
            nextExpiringCredit: action.data.nextExpiringCredit,
            loading: false,
            error: null,
          },
        },
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        balance: 0,
        balanceFetched: false,
        balanceError: true,
      },
      creditsByCurrency: {
        ...state.account.creditsByCurrency,
        [action.currencyCode]: {
          balance: 0,
          nextExpiringCredit: null,
          loading: false,
          error: true,
        },
      },
    }),
  },
  [FETCH_AVAILABLE_CREDIT_COLLECTION]: {
    request: (state) => ({
      account: {
        ...state.account,
        creditsCollection: { fetching: true },
      },
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        creditsCollection: { data: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        creditsCollection: { fetching: false, error: action.error },
      },
    }),
  },
  [FETCH_CUSTOMER_DETAILS]: {
    request: () => ({
      travellerDetailsProcessing: true,
      travellerDetailsFetched: false,
      travellerDetailsError: false,
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        addressDetails: action.data.addressDetails,
        gender: action.data.gender,
        title: action.data.title,
        middleName: action.data.middleName,
      },
      travellerDetailsProcessing: false,
      travellerDetailsFetched: true,
      travellerDetailsError: false,
    }),
    failure: () => ({
      travellerDetailsProcessing: false,
      travellerDetailsFetched: false,
      travellerDetailsError: true,
    }),
  },
  [DELETE_CUSTOMER_DETAILS]: {
    request: (state) => ({
      account: {
        ...state.account,
        addressDetails: undefined,
      },
      travellerDetailsProcessing: true,
      travellerDetailsFetched: false,
    }),
    success: () => ({
      travellerDetailsProcessing: false,
      travellerDetailsFetched: false,
    }),
    failure: () => ({
      travellerDetailsProcessing: false,
    }),
  },
  [UPDATE_CUSTOMER_DETAILS]: {
    request: () => ({
      travellerDetailsProcessing: true,
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        addressDetails: action.data.addressDetails,
        gender: action.data.gender,
        title: action.data.title,
        middleName: action.data.middleName,
      },
      travellerDetailsProcessing: false,
    }),
  },
  [FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION]: {
    request: (state) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: true,
              error: undefined,
            },
          },
        },
      },
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              item: action.data,
              fetching: false,
              error: undefined,
            },
          },
        },
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: false,
              error: action.error,
            },
          },
        },
      },
    }),
  },
  [UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION]: {
    request: (state) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: true,
              error: undefined,
            },
          },
        },
      },
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              item: action.data,
              fetching: false,
              error: undefined,
            },
          },
        },
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: false,
              error: action.error,
            },
          },
        },
      },
    }),
  },
  [CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW]: {
    request: (state) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: true,
              error: undefined,
            },
          },
        },
      },
    }),
    success: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              item: action.data,
              fetching: false,
              error: undefined,
            },
          },
        },
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              fetching: false,
              error: action.error,
            },
          },
        },
      },
    }),
  },
  [FETCH_USER_NOTIFICATIONS]: {
    request: () => ({
      fetchingNotifications: true,
    }),
    success: (state, action) => ({
      notifications: action.data,
      fetchingNotifications: false,
    }),
    failure: (state, action) => ({
      notificationsError: action.error,
      fetchingNotifications: false,
    }),
  },
  [FETCH_CUSTOMER_TRAVELLERS]: {
    request: () => ({
      travelCompanions: {
        fetching: true,
        data: EmptyArray,
      },
    }),
    success: (state, action) => ({
      travelCompanions: {
        fetching: false as const,
        data: action.data,
      },
    }),
    failure: () => ({
      travelCompanions: {
        fetching: false as const,
        error: 'Failed to fetch traveller companions',
      },
    }),
  },
  [DELETE_CUSTOMER_TRAVELLER]: {
    request: (state, action) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          deletionProcessing: {
            ...state.customerTravellers.submissionState.deletionProcessing,
            [action.id]: true,
          },
        },
      },
    }),
    success: (state, action) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          deletionProcessing: {
            ...state.customerTravellers.submissionState.deletionProcessing,
            [action.id]: false,
          },
        },
      },
      travelCompanions: state.travelCompanions.data ?
          {
            ...state.travelCompanions,
            data: state.travelCompanions.data.filter(companion => companion.id !== action.id),
          } :
        state.travelCompanions,
    }),
    failure: (state, action) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          deletionProcessing: {
            ...state.customerTravellers.submissionState.deletionProcessing,
            [action.id]: false,
          },
        },
      },
    }),
  },
  [SUBMIT_CUSTOMER_TRAVELLER]: {
    request: (state) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          error: false,
          submissionProcessing: 'processing',
        },
      },
    }),
    success: (state) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          submissionProcessing: 'processed',
          error: false,
        },
      },
    }),
    failure: (state) => ({
      customerTravellers: {
        ...state.customerTravellers,
        submissionState: {
          ...state.customerTravellers.submissionState,
          error: true,
          submissionProcessing: 'failed',
        },
      },
    }),
  },
  [FETCH_USER_CART]: {
    request: (state) => ({
      userCart: {
        ...userCartBase(state),
        fetching: true,
      },
    }),
    success: (state, action) => ({
      userCart: {
        ...userCartBase(state),
        items: action.data.items,
        connectedRoomOfferIds: filterConnectedRooms(action.data.items, action.data.connectedRoomOfferIds),
        itemStates: action.data.itemStates,
        fetching: false,
        synced: true,
      },
    }),
    failure: (state) => ({
      userCart: {
        ...userCartBase(state),
        fetching: false,
      },
    }),
  },
  [FETCH_USER_NUMBER_OF_REVIEWS]: {
    request: () => ({
      numberOfReviews: undefined,
    }),
    success: (_, action) => ({
      numberOfReviews: action.data.total,
    }),
  },
  [CHECK_LEGACY_ORDERS_FOR_CURRENT_USER]: {
    success: (state, action) => {
      return ({
        ledLegacyOrders: {
          ...state.ledLegacyOrders,
          hasAny: action.data.orders.length > 0,
          legacyCutoffDate: action.data.legacyCutoffDate,
          hasFetched: true,
          fetchLegacyOrdersFailed: false,
        },
      })
    },
    failure: (state) => ({
      ledLegacyOrders: {
        ...state.ledLegacyOrders,
        fetchLegacyOrdersFailed: true,
        hasFetched: false,
      },
    }),
  },
  [FETCH_PAYMENT_CARDS]: {
    success: (state, action) => ({
      account: {
        ...state.account,
        paymentCards: action.data,
        paymentCardsFetched: true,
        paymentCardsError: {},
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        paymentCardsError: action.error,
      },
    }),
  },
  [FETCH_STRIPE_PAYMENT_CARDS]: {
    success: (state, action) => ({
      account: {
        ...state.account,
        stripePaymentCards: action.data,
        stripePaymentCardsFetched: true,
        stripePaymentCardsError: {},
      },
    }),
    failure: (state, action) => ({
      account: {
        ...state.account,
        stripePaymentCardsError: action.error,
      },
    }),
  },
  [USER_LOGOUT]: {
    success: (state) => ({
      ...initialAuthState,
      domainUserId: state.domainUserId,
      gsiLoaded: state.gsiLoaded,
      initComplete: true,
    }),
  },
  [SET_RECENTLY_USED_AIRPORT_CODE]: {
    success: (state, action) => ({
      account: {
        ...state.account,
        recentlyUsedAirportCode: action.airportCode,
      },
    }),
  },
  [UPDATE_ADD_PHONE_PROMPTED]: {
    success: (state, action) => ({
      account: {
        ...state.account,
        addPhonePromptedAt: action.addPhonePromptedAt,
      },
    }),
  },
  [FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD]: {
    success: (state, action) => ({
      account: {
        ...state.account,
        luxPlus: {
          ...state.account.luxPlus,
          member: {
            ...state.account.luxPlus.member,
            subscription: {
              ...state.account.luxPlus.member.subscription,
              preferredPaymentCard: action.data,
            },
          },
        },
      },
    }),
  },
  [UPSERT_REVIEW]: {
    success: (state, action) => ({
      ...(action.isCreate && { numberOfReviews: (state.numberOfReviews ?? 0) + 1 }),
    }),
  },
})

export default createAppReducer<App.AuthState>(initialAuthState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [ACCOUNT_INIT_COMPLETE]: () => ({ initComplete: true }),
  [ACCOUNT_ACCESS_SHOW_LOGIN]: authClearError,
  [ACCOUNT_ACCESS_SHOW_EMAIL_JOIN]: authClearError,
  [ACCOUNT_ACCESS_SHOW_REGISTER]: authClearError,
  [ACCOUNT_ACCESS_SHOW_FORGOT_PASSWORD]: authClearError,
  [CLEAR_AUTH_ERROR]: authClearError,
  [SAVE_CUSTOMER_SIGNATURE]: (state, action) => ({
    signature: action.signature,
  }),
  [DELETE_CUSTOMER_SIGNATURE]: () => ({
    signature: undefined,
  }),
  [SYNC_USER_DETAILS]: (state, action) => ({
    account: {
      ...state.account,
      ...action.userDetails,
    },
  }),
  [GSI_SCRIPT_LOADED]: () => ({
    gsiLoaded: true,
  }),
  [SET_CUSTOMER_VALUE]: (state, action) => ({
    customerValue: action.value,
  }),
  [SUBSCRIPTION_USER_NOTIFICATIONS_UPDATE]: (state, action) => ({
    notifications: action.notifications,
  }),
  [REMOVE_USER_NOTIFICATION]: (state, action) => ({
    notifications: state.notifications.filter(item => item.id !== action.notification.id),
  }),
  [VIEW_USER_NOTIFICATIONS]: (state) => ({
    notifications: state.notifications.map(item => ({ ...item, viewed: true })),
  }),
  [FETCH_RECENT_SEARCHES]: (state, action) => ({
    recentSearches: action.data,
  }),
  [SET_RECENT_SEARCHES]: (state, action) => ({
    recentSearches: action.data,
  }),
  [TOGGLE_SHOP_AS_MEMBER]: (state, action) => ({
    account: {
      ...state.account,
      luxPlus: {
        ...state.account.luxPlus,
        member: {
          ...state.account.luxPlus.member,
          disableMembership: action.disableMembership,
        },
      },
    },
  }),
  [RESET_CUSTOMER_TRAVELLER_SUBMISSION]: (state) => ({
    customerTravellers: {
      ...state.customerTravellers,
      submissionState: {
        ...state.customerTravellers.submissionState,
        submissionProcessing: undefined,
        error: false,
      },
    },
  }),
  [ADD_USER_CART_ITEMS]: (state, action) => {
    const items = { ...state.userCart.items }
    const itemStates = { ...state.userCart.itemStates }

    // For item types that can only have one item in the cart, delete pre-existing one if we're adding one
    const newItemTypes = new Set(Object.values(action.items).map(item => item.itemType))
    const itemTypesToDelete = new Set<App.Checkout.AnyItem['itemType']>(NON_DUPLICABLE_ITEM_TYPES.filter(type => newItemTypes.has(type)))
    const itemIdsToDelete = Object.values(items).filter(item => itemTypesToDelete.has(item.itemType)).map(item => item.itemId)

    for (const id of itemIdsToDelete) {
      delete items[id]
      delete itemStates[id]
    }

    for (const item of action.items) {
      items[item.itemId] = item
      itemStates[item.itemId] = {
        saveForLater: false,
        deleted: false,
      }
    }

    return {
      userCart: {
        ...userCartBase(state),
        items,
        itemStates,
      },
    }
  },
  [ADD_USER_CART_CONNECTED_ROOMS]: (state, action) => ({
    userCart: {
      ...userCartBase(state),
      connectedRoomOfferIds: unique([
        ...state.userCart.connectedRoomOfferIds,
        ...action.connectedRoomOfferIds,
      ]),
    },
  }),
  [REMOVE_USER_CART_CONNECTED_ROOM]: (state, action) => ({
    userCart: {
      ...userCartBase(state),
      connectedRoomOfferIds: state.userCart.connectedRoomOfferIds.filter(id => id !== action.connectedRoomOfferId),
    },
  }),
  [CLEAR_PURCHASED_USER_CART_ITEMS]: (state, action) => {
    const items = omitKeys(Array.from<string>(action.purchasedItemIds))(state.userCart.items)
    const connectedRoomOfferIds = filterConnectedRooms(items, state.userCart.connectedRoomOfferIds)
    return {
      userCart: {
        ...userCartBase(state),
        items,
        connectedRoomOfferIds,
      },
    }
  },
  [SYNC_USER_CART]: (_, action) => ({
    userCart: {
      ...action.userCartState,
      synced: true,
    },
  }),
  [SAVE_USER_CART_ITEMS_FOR_LATER]: (state, action) => {
    if (action.itemIds.length === 0) {
      return state
    }
    // In case the items are initially in different states - we want to toggle them all on/off together,
    // so determine the new state from the first item and apply that to all of them
    const newValue = !state.userCart.itemStates?.[action.itemIds[0]]?.saveForLater
    return ({
      userCart: {
        ...userCartBase(state),
        itemStates: {
          ...action.itemIds.reduce<App.UserCartState['itemStates']>((itemStates, itemId) => {
            itemStates[itemId] = {
              deleted: state.userCart.itemStates?.[itemId]?.deleted ?? false,
              saveForLater: newValue,
            }
            return itemStates
          }, { ...state.userCart.itemStates }),
        },
      },
    })
  },
  [SAVE_ALL_USER_CART_ITEMS_FOR_LATER]: (state) => ({
    userCart: {
      ...userCartBase(state),
      itemStates: mapObject(
        state.userCart.itemStates,
        itemState => (
          !itemState?.deleted && !itemState?.saveForLater ? { deleted: false, saveForLater: true } : itemState
        ),
      ),
    },
  }),
  [UPDATE_USER_CART_ITEM]: (state, action) => ({
    userCart: {
      ...userCartBase(state),
      items: {
        ...state.userCart.items,
        [action.item.itemId]: action.item,
      },
    },
  }),
  [DELETE_USER_CART_ITEMS]: (state, action) => ({
    userCart: {
      ...userCartBase(state),
      itemStates: {
        ...action.itemIds.reduce<App.UserCartState['itemStates']>((itemStates, itemId) => {
          itemStates[itemId] = {
            saveForLater: false,
            deleted: true,
          }
          return itemStates
        }, { ...state.userCart.itemStates }),
      },
    },
  }),
  [HARD_DELETE_USER_CART_ITEMS]: (state, action) => {
    if (!action.itemIds.some(id => state.userCart.items[id])) {
      return state
    }
    return {
      userCart: {
        ...userCartBase(state),
        items: omitKeys(action.itemIds, state.userCart.items),
        itemStates: omitKeys(action.itemIds, state.userCart.itemStates),
      },
    }
  },
  // this does pretty much the same as the above but makes the intent much clearer in code
  [UNDO_USER_CART_ITEMS_DELETE]: (state, action) => ({
    userCart: {
      ...userCartBase(state),
      itemStates: {
        ...action.itemIds.reduce<App.UserCartState['itemStates']>((itemStates, itemId) => {
          itemStates[itemId] = {
            saveForLater: itemStates[itemId]?.saveForLater ?? false,
            deleted: false,
          }
          return itemStates
        }, { ...state.userCart.itemStates }),
      },
    },
  }),
  [RESET_USER_CART]: () => ({
    userCart: initialAuthState.userCart,
  }),
  [FETCH_USER_NUMBER_OF_REVIEWS]: () => ({
    numberOfReviews: undefined,
  }),
})
